import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import React from 'react';
import PropTypes from 'prop-types';
import createThemedComponent from '../../../theming/utils/createThemedComponent';
import { PinterestIcon } from './PinterestShareButton.styles';
import usePinterestShare from '../../hooks/usePinterestShare';
import IconButton from '../IconButton';
/**
 * Themeable PinterestShareButton component which provides pinterest sharing functionality with the click of a button.
 *
 * By default, it logs 1 piwik event: `event(['share', 'pinterest', <PAGE_BEING_SHARED>]);`, but you can pass in a custom `piwikEvent` to log instead.
 *
 * This event is logged when the user begins to share with pinterest. If choosing to continue with the default piwik events provided, a value must be provided for the `page` prop.
 *
 * If a value is passed for the `piwikEvent` prop, then the button onClick will log that custom event instead.
 *
 *```js
 * import { PinterestShareButton } from '@minerva-analytics/pantheon';
 *
 * // Without custom piwik event
 * <PinterestShareButton
 *   url="https://minervaanalytics.co"
 *   description="Check out this new website I just found!"
 *   media="minerva-logo.jpg"
 *   appId="12345"
 *   page="example-page"
 * />
 *
 * // With custom piwik event
 * <PinterestShareButton
 *   url="https://minervaanalytics.co"
 *   description="Check out this new website I just found!"
 *   media="minerva-logo.jpg"
 *   appId="12345"
 *   piwikEvent={['click', 'share']}
 * />
 *```
 *
 * **Default Styles**
 *
 *To view the `PinterestShareButton` styling directly, see [PinterestShareButton.styles.js](https://git.cogolo.net/minerva/pantheon/blob/master/src/components/widgets/PinterestShareButton/PinterestShareButton.styles.js)
 *
 **/

export var PinterestShareButtonComponent = function PinterestShareButtonComponent(_ref) {
  var media = _ref.media,
      url = _ref.url,
      description = _ref.description,
      appId = _ref.appId,
      page = _ref.page,
      piwikEvent = _ref.piwikEvent,
      rest = _objectWithoutPropertiesLoose(_ref, ["media", "url", "description", "appId", "page", "piwikEvent"]);

  var share = usePinterestShare({
    url: url,
    description: description,
    media: media,
    page: page,
    appId: appId,
    piwikEvent: piwikEvent
  });
  return /*#__PURE__*/React.createElement(IconButton, _extends({
    onClick: function onClick() {
      return share();
    }
  }, rest), /*#__PURE__*/React.createElement(PinterestIcon, null));
};
PinterestShareButtonComponent.propTypes = {
  /** URL that user is intending to share */
  url: PropTypes.string.isRequired,

  /** Custom description for share  */
  description: PropTypes.string.isRequired,

  /** Custom image for share */
  media: PropTypes.string.isRequired,

  /** Pinterest app ID */
  appId: PropTypes.string.isRequired,

  /** Page that the pinterest share button will live on (used for piwik event tracking) */
  page: PropTypes.string,

  /** Optional custom event that is logged upon sharing with pinterest. Must be an array in the form of `[category, action, name, value]`, though `name` and `value` are optional. */
  piwikEvent: PropTypes.array
};
PinterestShareButtonComponent.defaultProps = {
  piwikEvent: ['share', 'pinterest', '<PAGE_BEING_SHARED>']
};
var PinterestShareButton = createThemedComponent('PinterestShareButton', PinterestShareButtonComponent);
export default PinterestShareButton;